import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, connect } from 'react-redux';

import RoundedModal from 'components/Core/Modal/RoundedModal/RoundedModal';
import NpeFreeTextModal from 'components/Core/NonPayEnabled/UnpayableCard/Growth/NpeFreeTextExperiment/NpeFreeTextModal/NpeFreeTextModal';
import {
  EmailModalGetters,
  EmailModalSetters,
} from 'components/Core/NonPayEnabled/UnpayableCard/Growth/consts';
import Button from 'components/Shared/Button/Button';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import SegmentIO from 'reporting/SegmentIO';
import { ContactInfoType } from 'shared/types';
import { insightSelectors } from 'store/insight/selectors';
import { insightActions, sendNpeFreeTextEmailEvent } from 'store/insight/slice';
import { modalActions } from 'store/modal/slice';
import { RootState } from 'store/store';
import { useSelector } from 'store/utils';
import { Modal } from 'types/Modal';

interface SendMerchantMessageModalProps {
  companyName: string;
  merchantEmail: string;
  customerEmail?: string;
  trigger: (open: () => void) => React.ReactNode;
  onSuccess?: () => void;
  onError?: (e: any) => void;
  showModal: (Component: Pick<Modal, 'component' | 'componentProps'>, args?: Object) => void;
  hideModal: () => void;
  showBackToChatButton?: boolean;
}

const NpeFreeTextSuccessModal = ({
  hide,
  showBackToChartButton = false,
}: {
  hide: () => void;
  showBackToChartButton?: boolean;
}) => {
  const cdn = useSelector((state: any) => state.config?.endpoints?.cdn);
  const isMobile = useIsMobileScreen(768);
  return (
    <RoundedModal
      hide={hide}
      header={null}
      removeDefaultBackground={true}
      customDesignKey="freeTextModal"
      isCloseVisible={true}
      className="npe-success-modal"
    >
      <>
        <div className="success-message" data-testid="success-message">
          <img src={`${cdn}/feedback_success.png`} alt="Success" />
          <p>
            <FormattedMessage
              id="GROWTH_FREE_TEXT_EMAIL_FORM_SUCCESS.MESSAGE"
              defaultMessage="Your message is sent!"
            />
          </p>
          <div className="buttons">
            {showBackToChartButton && (
              <Button onClick={hide}>
                <FormattedMessage
                  id="GROWTH_FREE_TEXT_EMAIL_FORM_SUCCESS.BACK_TO_CHAT"
                  defaultMessage="Back to chat"
                />
              </Button>
            )}
            <Button buttonType="primary" onClick={hide} width={'100px'}>
              <FormattedMessage
                id="GROWTH_FREE_TEXT_EMAIL_FORM_SUCCESS.CLOSE"
                defaultMessage="Close"
              />
            </Button>
          </div>
        </div>
        {/*language=SCSS*/}
        <style jsx>{`
          .success-message {
            padding: 32px;
            text-align: center;
            min-width: ${isMobile ? '320px' : '672px'};
            font-weight: 600;
            font-size: 20px;
          }
          .buttons {
            margin: 30px 0;
            display: flex;
            justify-content: center;
            gap: 16px;
          }
        `}</style>
      </>
    </RoundedModal>
  );
};

const SendMerchantMessageModal: React.FC<SendMerchantMessageModalProps> = ({
  companyName,
  merchantEmail,
  customerEmail,
  trigger,
  onSuccess,
  onError,
  showModal,
  hideModal,
  showBackToChatButton = false,
}) => {
  const dispatch = useDispatch();
  const [freeTextSenderName, setFreeTextSenderName] = useState<string>('');
  const [freeTextSenderContactInfo, setFreeTextSenderContactInfo] = useState<string>(
    customerEmail || ''
  );
  const [freeTextSenderContactType, setFreeTextSenderContactType] =
    useState<ContactInfoType>('Email');
  const [freeTextSenderContactInfoPhone, setFreeTextSenderContactInfoPhone] = useState<string>('');
  const [freeTextMessage, setFreeTextMessage] = useState<string>('');
  const freeTextEmailOINPEventStatus = useSelector((state: RootState) =>
    insightSelectors.freeTextEmailOINPEventStatusSelector(state.insight)
  );

  const domainId = useSelector((state: RootState) => state.insight?.domainId || state.sale?.id);

  useEffect(() => {
    if (freeTextEmailOINPEventStatus === 'SENT_SUCCESSFULLY') {
      dispatch(modalActions.hide());
      resetState();
      showModal({
        component: (props) => (
          <NpeFreeTextSuccessModal
            {...props}
            hide={() => {
              hideModal();
              dispatch(insightActions.resetFreeTextEmailOINPEventStatus());
            }}
            showBackToChartButton={showBackToChatButton}
          />
        ),
        componentProps: {
          closeOnClickOutSide: false,
          closeOnEsc: true,
          showCloseButton: false,
        },
      });
    }
  }, [freeTextEmailOINPEventStatus, showModal, hideModal, showBackToChatButton, dispatch]);

  const handleSetFreeTextSenderContactType = (type: ContactInfoType) => {
    setFreeTextSenderContactType(type);
    if (type === 'Email') {
      setFreeTextSenderContactInfo(freeTextSenderContactInfoPhone ? '' : customerEmail || '');
    } else {
      setFreeTextSenderContactInfoPhone(freeTextSenderContactInfo);
      setFreeTextSenderContactInfo('');
    }
  };

  const resetState = () => {
    setFreeTextSenderName('');
    setFreeTextSenderContactInfo(customerEmail || '');
    setFreeTextSenderContactType('Email');
    setFreeTextSenderContactInfoPhone('');
    setFreeTextMessage('');
  };

  const handleClickSend = useCallback(async () => {
    try {
      await (dispatch as any)(
        sendNpeFreeTextEmailEvent({
          merchantEmail: merchantEmail || '',
          senderName: freeTextSenderName || '',
          senderContactInfo: freeTextSenderContactInfo || '',
          freeTextMessage: freeTextMessage || '',
        })
      );

      SegmentIO.aiAssistMessageSentModal({
        domain_id: domainId,
      });

      if (onSuccess) onSuccess();
    } catch (e) {
      if (onError) onError(e);
    }
  }, [
    freeTextSenderName,
    freeTextSenderContactInfo,
    freeTextMessage,
    merchantEmail,
    domainId,
    dispatch,
    onSuccess,
    onError,
  ]);

  const sendRef = useRef(handleClickSend);
  sendRef.current = handleClickSend;

  const openModal = () => {
    dispatch(
      modalActions.show({
        component: () => (
          <NpeFreeTextModal
            companyName={companyName || ''}
            hide={() => {
              dispatch(modalActions.hide());
              dispatch(insightActions.resetFreeTextEmailOINPEventStatus());
              resetState();
            }}
            onContinue={() => sendRef.current()}
            emailModalSetters={
              {
                setFreeTextSenderName,
                setFreeTextSenderContactInfo,
                setFreeTextSenderContactType: handleSetFreeTextSenderContactType,
                setFreeTextMessage,
              } as EmailModalSetters
            }
            emailModalGetters={
              {
                freeTextSenderName: freeTextSenderName || '',
                freeTextSenderContactInfo: freeTextSenderContactInfo || '',
                freeTextSenderContactType,
                freeTextMessage: freeTextMessage || '',
              } as EmailModalGetters
            }
            customerEmail={customerEmail}
          />
        ),
        componentProps: {
          closeOnClickOutSide: false,
          closeOnEsc: false,
          showCloseButton: false,
        },
      })
    );
  };

  return <>{trigger(openModal)}</>;
};

export default connect(null, {
  showModal: modalActions.show,
  hideModal: modalActions.hide,
})(SendMerchantMessageModal);
